// In-app walkthroughs (18-help-and-training.md). One walkthrough per page. A person sees only the
// walkthroughs of the pages they can open, and inside a walkthrough only the steps their permissions
// allow, so a customer-care agent never learns the cashier's screens.
import type { Permission } from './permissions';

/** The app a walkthrough belongs to. */
export const HELP_APPS = ['office', 'ops', 'partner-link'] as const;
export type HelpApp = (typeof HELP_APPS)[number];

/** How the walkthrough was taken: step by step, or watched as an automatic demo. */
export const WALKTHROUGH_MODES = ['guided', 'demo'] as const;
export type WalkthroughMode = (typeof WALKTHROUGH_MODES)[number];

/** How it ended: every step seen, or closed early. */
export const WALKTHROUGH_OUTCOMES = ['completed', 'skipped'] as const;
export type WalkthroughOutcome = (typeof WALKTHROUGH_OUTCOMES)[number];

export const WALKTHROUGH_ID = /^[a-z][a-z0-9-]{1,63}$/;
export const TOUR_TARGET = /^[a-z][a-z0-9-]{0,47}$/;
export const STEP_TITLE_MAX = 60;
export const STEP_BODY_MAX = 280;
export const MIN_STEPS = 2;
export const MAX_STEPS = 12;

export interface WalkthroughStep {
  /**
   * The `data-tour` value of the element the step points at. Omit for a step with no pointer
   * (shown as a centred card), such as the opening step.
   */
  target?: string;
  /** Short, plain words. At most 60 characters. */
  title: string;
  /** Plain text, never HTML. One or two sentences, at most 280 characters. */
  body: string;
  /** Shown only to people holding one of these. Omit for everyone who can open the page. */
  anyOf?: readonly Permission[];
  /** Optional nudge for the guided mode, such as "Type a tracking number here". */
  tryIt?: string;
}

export interface Walkthrough {
  /** The page id. In the office it is the menu id (`cargo-new`); in the phone app `ops-<page>`. */
  id: string;
  app: HelpApp;
  /** The page's title as it appears on screen. */
  pageTitle: string;
  /** One sentence: what the page is for. */
  summary: string;
  /** Raise it when the steps change in a way people should see again. Starts at 1. */
  version: number;
  /**
   * Who can open the page. In the office this comes from the menu entry and is left out here; the
   * phone app's pages have no menu, so they say it themselves. Omit for everyone.
   */
  anyOf?: readonly Permission[];
  steps: readonly WalkthroughStep[];
}

/** What the server keeps for one person and one walkthrough. */
export interface WalkthroughRecord {
  version: number;
  outcome: WalkthroughOutcome;
  mode: WalkthroughMode;
  /** ISO time it ended. */
  at: string;
}

/** A person's records, by walkthrough id. */
export type TrainingRecords = Readonly<Record<string, WalkthroughRecord>>;

/**
 * - `new`: never taken.
 * - `done`: completed at the current version.
 * - `updated`: completed before, but the walkthrough has changed since.
 * - `skipped`: closed early at the current version, never completed.
 */
export type WalkthroughStatus = 'new' | 'done' | 'updated' | 'skipped';

const holdsAny = (held: ReadonlySet<Permission>, anyOf: readonly Permission[] | undefined) =>
  !anyOf || anyOf.length === 0 || anyOf.some((p) => held.has(p));

/** Whether a person with these permissions can open the walkthrough's page. */
export function canSeeWalkthrough(w: Walkthrough, held: ReadonlySet<Permission>): boolean {
  return holdsAny(held, w.anyOf);
}

/** The steps this person is shown, in order. */
export function visibleSteps(
  w: Walkthrough,
  held: ReadonlySet<Permission>,
): readonly WalkthroughStep[] {
  return w.steps.filter((s) => holdsAny(held, s.anyOf));
}

export function walkthroughStatus(
  w: Walkthrough,
  record: WalkthroughRecord | undefined,
): WalkthroughStatus {
  if (!record) return 'new';
  if (record.outcome === 'completed') return record.version >= w.version ? 'done' : 'updated';
  return record.version >= w.version ? 'skipped' : 'new';
}

/** Whether the page should offer the walkthrough by itself on a visit (the first-visit nudge). */
export function shouldOfferWalkthrough(
  w: Walkthrough,
  record: WalkthroughRecord | undefined,
): boolean {
  const status = walkthroughStatus(w, record);
  return status === 'new' || status === 'updated';
}

export interface TrainingPlanItem {
  walkthrough: Walkthrough;
  status: WalkthroughStatus;
  record: WalkthroughRecord | undefined;
}

export interface TrainingPlan {
  items: readonly TrainingPlanItem[];
  done: number;
  total: number;
  /** 0 to 100, whole numbers. 100 when there is nothing to learn. */
  percent: number;
}

/**
 * A person's training: the walkthroughs of the pages they can open (already filtered by the
 * caller, such as by the office menu), each with its status.
 */
export function trainingPlan(
  walkthroughs: readonly Walkthrough[],
  records: TrainingRecords,
): TrainingPlan {
  const items = walkthroughs.map((walkthrough) => {
    const record = records[walkthrough.id];
    return { walkthrough, record, status: walkthroughStatus(walkthrough, record) };
  });
  const done = items.filter((i) => i.status === 'done').length;
  const total = items.length;
  return { items, done, total, percent: total === 0 ? 100 : Math.floor((done * 100) / total) };
}

/** Problems with a walkthrough's content, in plain words. Empty when it is fine. */
export function walkthroughProblems(
  w: Walkthrough,
  knownPermissions: ReadonlySet<string>,
): string[] {
  const problems: string[] = [];
  if (!WALKTHROUGH_ID.test(w.id)) problems.push(`${w.id}: the id is not a page id`);
  if (!Number.isInteger(w.version) || w.version < 1)
    problems.push(`${w.id}: the version must be a whole number from 1`);
  if (w.pageTitle.trim() === '') problems.push(`${w.id}: the page title is empty`);
  if (w.summary.trim() === '') problems.push(`${w.id}: the summary is empty`);
  if (w.steps.length < MIN_STEPS || w.steps.length > MAX_STEPS)
    problems.push(`${w.id}: has ${w.steps.length} steps; it needs ${MIN_STEPS} to ${MAX_STEPS}`);
  for (const p of w.anyOf ?? [])
    if (!knownPermissions.has(p)) problems.push(`${w.id}: unknown permission ${p}`);
  w.steps.forEach((s, i) => {
    const at = `${w.id} step ${i + 1}`;
    if (s.target !== undefined && !TOUR_TARGET.test(s.target))
      problems.push(`${at}: the target "${s.target}" is not a data-tour name`);
    if (s.title.trim() === '' || s.title.length > STEP_TITLE_MAX)
      problems.push(`${at}: the title must be 1 to ${STEP_TITLE_MAX} characters`);
    if (s.body.trim() === '' || s.body.length > STEP_BODY_MAX)
      problems.push(`${at}: the text must be 1 to ${STEP_BODY_MAX} characters`);
    if (/[<>]/.test(s.title + s.body + (s.tryIt ?? '')))
      problems.push(`${at}: plain text only, no < or >`);
    for (const p of s.anyOf ?? [])
      if (!knownPermissions.has(p)) problems.push(`${at}: unknown permission ${p}`);
  });
  // Everyone who can open the page must see at least two steps.
  const everyone = w.steps.filter((s) => !s.anyOf || s.anyOf.length === 0);
  if (everyone.length < MIN_STEPS)
    problems.push(
      `${w.id}: only ${everyone.length} steps are shown to everyone; it needs ${MIN_STEPS}`,
    );
  return problems;
}

/** Roughly how long the walkthrough takes, in whole minutes, at least 1. */
export function walkthroughMinutes(steps: readonly WalkthroughStep[]): number {
  const words = steps.reduce((n, s) => n + `${s.title} ${s.body}`.split(/\s+/).length, 0);
  // About 150 words a minute, plus a few seconds to look at each thing pointed at.
  return Math.max(1, Math.round(words / 150 + (steps.length * 4) / 60));
}

/** How long the demo stays on a step, in milliseconds: time to read it, between 4 and 12 seconds. */
export function demoStepMs(step: WalkthroughStep): number {
  const words = `${step.title} ${step.body}`.split(/\s+/).length;
  return Math.min(12_000, Math.max(4_000, Math.round((words / 3.2) * 1000)));
}

/**
 * The record kept after a new one arrives (18 §18.4). A later record replaces an earlier one,
 * except that a skip never replaces a completion of the same or a later version. Commands sent
 * offline can arrive out of order, so "later" is by `at`, the time the walkthrough ended, and the
 * answer is the same whichever of two records arrives first.
 */
export function mergeWalkthroughRecord(
  existing: WalkthroughRecord | undefined,
  next: WalkthroughRecord,
): WalkthroughRecord {
  if (!existing) return next;
  const covers = (a: WalkthroughRecord, b: WalkthroughRecord) =>
    a.outcome === 'completed' && b.outcome === 'skipped' && a.version >= b.version;
  if (covers(existing, next)) return existing;
  if (covers(next, existing)) return next;
  return next.at >= existing.at ? next : existing;
}
